<template>
    <div>

        <div v-if="Object.keys(standards).length" class="card mb-3">
            <div class="card-header d-flex align-items-center">
                <div class="flex-grow-1">
                    <h5 class="mb-0">{{ standards[activeStandard].code }}
                        <span v-if="standards[activeStandard].subcode">({{ standards[activeStandard].subcode }})</span>
                    </h5>
                    <h6 class="text-muted mb-0">{{ standards[activeStandard].strand }}</h6>
                </div>
                <a class="btn" data-bs-toggle="collapse" href="#standard-collapse" role="button" aria-expanded="false" aria-controls="standard-collapse">
                    <i class="fas fa-chevron-down"></i>
                </a>
            </div>
            <div class="collapse" id="standard-collapse">
                <div class="card-body" style="white-space: pre-wrap;">{{ standards[activeStandard].content }}</div>
            </div>
        </div>

        <worksheet-opportunity v-model="opportunity" @input="update" @remove="remove"></worksheet-opportunity>

        <div class="table-responsive">

            <table class="table table-bordered">
                <tr>
                    <th></th>
                    <th v-for="(std, idx) in standards" 
                        class="px-1 border-start"
                        scope="col" 
                        :class="idx == activeStandard ? 'table-primary' : ''" 
                        @click="activeStandard = idx"
                    >
                    {{ std.code }}<span v-if="std.subcode" class="p-0 ps-1">{{ std.subcode }}</span></th>
                </tr>
                <tr v-for="(student) in students">
                    <th scope="row">{{ student.last_name }}, {{ student.first_name }}</th>
                    <td v-for="(standard) in standards" class="border-start">
                        <worksheet-assessment 
                            :value="getAssessment(standard.id, student.id)"
                            :evaluation_date="evaluation_date"
                            :worksheets_dates_id="id"
                            @create-worksheet-date="create"
                            @create="add"
                            @remove="sub"
                        ></worksheet-assessment>
                    </td>
                </tr>
            </table>

        </div>
    </div>
</template>

<script>
import WorksheetOpportunity from './WorksheetOpportunity.vue';
import WorksheetAssessment from './WorksheetAssessment.vue';

export default {
    components: { 
        WorksheetOpportunity,
        WorksheetAssessment, 
    },

    props: {
        standards: {
            type: Array,
            default: function(data=[]) {
                return data;
            },
        },

        students: {
            type: Array,
            default: function(data=[]) {
                return data;
            }
        },

        worksheetDate: {
            type: Object,
            default: function(data={}) {
                return data;
            },
        },

        worksheet: {
            type: Number,
            default: null,
        },
    },

    watch: {
        /**
         * This is required to trigger the refresh when the date is switched
         * 
         * @param n 
         * @param o 
         */
        worksheetDate: function(n, o) {
            this.id = n.id ?? null;
            this.opportunity = n.assessment_opportunity ?? '';
            this.assessments = n.assessments ?? [];
            this.evaluation_date = n.evaluation_date;
        },

        worksheet: function(n, o) {
            if (this.pending) {
                this.create();
            }
        }
    },

    updated: function() {
        console.log('DATE UPDATED');
    },

    data() {
        return {
            opportunity: this.worksheetDate.assessment_opportunity ?? '',
            assessments: this.worksheetDate.assessments ?? [],
            id: this.worksheetDate.id ?? null,
            evaluation_date: this.worksheetDate.evaluation_date,

            activeStandard: 0,
            pending: false,
        };
    },

    methods: {
        create: function() {
            if (this.worksheet) {
                const data = {
                    worksheets_id: this.worksheet,
                    evaluation_date: this.evaluation_date,
                    assessment_opportunity: this.opportunity,
                };

                axios
                    .post('/api/worksheet/date', data)
                    .then(response => {
                        this.$emit('create', response.data);
                        this.id = response.data.id;
                        console.debug(response.data);
                    })
                    .catch(err => {
                        console.error(err);
                    })
                    .finally(() => {
                        this.$parent.isLoading = false;
                        this.pending = false;
                    });
            } else {
                this.pending = true;
                this.$emit('create-worksheet');
            }
        },

        update: function() {
            const data = {
                assessment_opportunity: this.opportunity,
            };

            axios
                .patch(`/api/worksheet/date/${this.id}`, data)
                .then(response => {
                    this.$emit('create', response.data);
                    this.id = response.data.id;
                    console.debug(response.data);
                })
                .catch(err => {
                    console.error(err);
                })
                .finally(() => {
                    this.$parent.isLoading = false;
                    this.pending = false;
                });
        },

        /**
         * If there is no content left on this date, delete it
         */
        remove: function() {
            if (this.opportunity || this.assessments.length) {
                console.debug('Worksheet date has content. Not deleting.');
            } else {
                console.debug('Deleting worksheet date...');
                axios
                    .delete(`/api/worksheet/date/${this.id}`)
                    .then(response => {
                        this.$emit('remove', this.id);
                        this.id = null;
                    })
                    .catch(err => {
                        console.error(err);
                    })
                    .finally(() => {
                        this.$parent.isLoading = false;
                    });
            }

        },

        /**
         * Add a new assessment to the assessments array
         * 
         * @param assessment 
         */
        add: function(assessment)  {
            this.assessments.push(assessment);
        },

        /**
         * Remove the deleted assessment from the assessment array
         * 
         * This also attempts to delete the worksheet_date
         * 
         * @param assessment_id 
         */
        sub: function(assessment_id) {
            this.assessments = this.assessments.filter((a) => a.id != assessment_id);
            this.remove();
        },

        /**
         * Get the assessment from the array, or a blank array
         * 
         * @param standard
         * @param student 
         */
        getAssessment: function(standard, student) {
            const newAssessment = {
                standards_id: standard,
                students_id: student,
                worksheets_dates_id: this.worksheetDate.id,
            };

            return this.assessments.find(assess => assess.standards_id == standard && assess.students_id == student) ?? newAssessment;
        },
    },
}
</script>

<style scoped>
th {
    width: 0.1%;
    white-space: nowrap;
}

.table-primary {
    background-color: #cfe2ff;
}

table {
    overflow-x: scroll;
}

</style>